import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useSelector } from '@tanstack/react-form';
import {
  IonButton,
  IonDatetime,
  IonDatetimeButton,
  IonInput,
  IonModal,
  IonSpinner,
  useIonAlert,
} from '@ionic/react';
import { ApiError } from '@shared/auth';
import { cn, haptic, toDateKeyFromISO } from '@shared/utils';
import type {
  DietEntry,
  DietEntryDraft,
  MealSlot,
  MeasurementUnit,
  EstimateQuota,
  EstimateResult,
  RecentItem,
} from '@diet/types';
import {
  currentSlot,
  formatCalories,
  hasMacros,
  MEAL_SLOT_LABELS,
  MEAL_SLOTS,
} from '@diet/utils';
import {
  FIELD_CLASS,
  ProgressOverlay,
  SegmentedControl,
  SheetLayout,
  SheetModal,
} from '@mobile/ui';
import {
  CHIP_CLASS,
  FieldError,
  LABEL_CLASS,
  SparkleIcon,
  TrashIcon,
} from '@shared/ui';
import { NutrientSection } from './nutrient-section';
import type { EstimateMeta } from './nutrient-section';

export interface EntryFormSheetProps {
  /** 面板是否開啟。IonModal 為常駐元件，以此控制而非條件渲染 */
  isOpen: boolean;
  /** 有值為編輯既有紀錄，null 為新增 */
  entry: DietEntry | null;
  /** 新增時的預設日期（目前檢視的那天） */
  defaultDate: string;
  /**
   * 查詢某天某餐別最近輸入過的品項，供一鍵帶入。
   *
   * 收函式而非算好的陣列：候選取決於表單當下的日期與餐別，那兩個值是本元件的狀態，
   * 呼叫端無從得知。
   */
  getRecentItems: (dateKey: string, slot: MealSlot) => RecentItem[];
  /**
   * 送出紀錄。**必須回傳 Promise**——面板要靠它判斷何時解除進行中狀態，
   * 型別若是 `void`，重複送出就擋不掉。
   */
  onSubmit: (draft: DietEntryDraft) => Promise<void>;
  /** 刪除紀錄。同樣必須回傳 Promise，理由同 `onSubmit` */
  onDelete?: (id: string) => Promise<void>;
  /**
   * 以 AI 估算營養成分。
   *
   * 由呼叫端注入而非在此直接呼叫 repository：這個元件只管畫面與表單狀態，
   * 資料取得留在外面，Storybook 與測試才餵得進假資料。
   */
  onEstimate: (
    foodName: string,
    quantity: number,
    unit: MeasurementUnit,
  ) => Promise<EstimateResult>;
  /** 取今日剩餘估算次數，用於在按下之前就顯示還能按幾次 */
  onFetchQuota: () => Promise<EstimateQuota>;
  onClose: () => void;
}

const FORM_ID = 'entry-form';

/**
 * 新增與編輯共用的底部面板。
 *
 * 只有品項與熱量必填；日期預設為檢視中的日期、餐別依當下時刻預選，
 * 使用者多數情況只需輸入兩個欄位即可送出。
 *
 * 外殼交給 `IonModal` 的 sheet 模式，由它負責拖曳把手、停靠、swipe-to-dismiss、
 * 焦點鎖定與鍵盤讓位；此處只處理表單本身。
 */
export function EntryFormSheet({
  isOpen,
  entry,
  defaultDate,
  getRecentItems,
  onSubmit,
  onDelete,
  onEstimate,
  onFetchQuota,
  onClose,
}: EntryFormSheetProps) {
  /*
   * 估算狀態由面板持有而非表單：`canDismiss` 掛在 IonModal 上，
   * 只保留一份才能確保「顯示估算中」與「面板不可關閉」一致。
   */
  const [isEstimating, setIsEstimating] = useState(false);

  return (
    <SheetModal
      isOpen={isOpen}
      /*
       * 面板收掉時一併重置。正常情況下估算結束才可能關閉，但萬一因為別的路徑卸載
       * （session 過期、外部把 isOpen 轉 false），殘留的 true 會讓面板下次再也關不掉。
       */
      onClose={() => {
        setIsEstimating(false);
        onClose();
      }}
      /*
       * 估算中不可關閉。面板一旦收掉，表單就卸載了，回來的結果無處可填 ——
       * 但額度已經扣掉，使用者等於付了一次換到空白。
       */
      canDismiss={!isEstimating}
      /* 留下約五分之一露出底下的紀錄，維持「疊加」而非「換頁」的感覺 */
      heightClass="[--height:78%]"
    >
      <EntryForm
        entry={entry}
        defaultDate={defaultDate}
        getRecentItems={getRecentItems}
        onSubmit={onSubmit}
        onDelete={onDelete}
        onEstimate={onEstimate}
        onFetchQuota={onFetchQuota}
        isEstimating={isEstimating}
        onEstimatingChange={setIsEstimating}
        onClose={onClose}
      />
    </SheetModal>
  );
}

type EntryFormProps = Omit<EntryFormSheetProps, 'isOpen'> & {
  /* 由面板持有，因為 canDismiss 掛在 IonModal 上（見上方說明） */
  isEstimating: boolean;
  onEstimatingChange: (value: boolean) => void;
};

/**
 * 表單的值。
 *
 * 數值欄位（熱量、份量、營養素）一律存成字串：輸入途中會經過 `''` 與 `'12.'`
 * 這種還不是數字的狀態，轉成數字留到送出時由 `onSubmit` 處理。
 */
interface EntryFormValues {
  name: string;
  calories: string;
  quantity: string;
  unit: MeasurementUnit;
  proteinG: string;
  fatG: string;
  carbsG: string;
  fiberG: string;
  sodiumMg: string;
  date: string;
  slot: MealSlot;
}

function validateName({ value }: { value: string }) {
  return value.trim() === '' ? '請輸入品項名稱' : undefined;
}

/**
 * 選填數值的驗證。空字串一律通過 —— 未填是合法狀態。
 *
 * 上限與後端一致（見 diet-service 的 EntryRequest）：不是營養學標準，是擋住
 * 打錯一個零之類的離譜輸入。兩邊都擋，是因為前端擋得早、後端擋得住。
 */
function validateOptionalAmount(max: number) {
  return ({ value }: { value: string }) => {
    if (value.trim() === '') return undefined;

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '請輸入數字';
    if (parsed < 0) return '不能是負數';
    if (parsed > max) return `不能超過 ${max}`;

    return undefined;
  };
}

const validateGrams = validateOptionalAmount(1000);
const validateSodium = validateOptionalAmount(20000);

/** 數值轉回表單字串；null（未填）對應空字串。 */
function toFieldValue(amount: number | null | undefined): string {
  return amount === null || amount === undefined ? '' : String(amount);
}

/** 表單字串轉成送出用的數值；空字串是 null（未填），不是 0。 */
function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * 份量驗證。與後端一致：0.01–10000、小數點後最多兩位。
 *
 * 小數位數用字串判斷而非數值運算 —— `0.07 * 100` 在浮點數裡不是 7，
 * 拿它去取餘數會得到假的「小數過多」。
 */
function validateQuantity({ value }: { value: string }) {
  const trimmed = value.trim();
  if (trimmed === '') return '請輸入份量';

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return '請輸入數字';
  if (parsed < 0.01) return '最少 0.01';
  if (parsed > 10000) return '最多 10000';

  const decimals = trimmed.split('.')[1];
  if (decimals !== undefined && decimals.length > 2) return '最多兩位小數';

  return undefined;
}

function validateCalories({ value }: { value: string }) {
  if (value.trim() === '') return '請輸入熱量';

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '請輸入熱量';
  if (parsed < 0) return '熱量不能是負數';

  return undefined;
}

function EntryForm({
  entry,
  defaultDate,
  getRecentItems,
  onSubmit,
  onDelete,
  onEstimate,
  onFetchQuota,
  isEstimating,
  onEstimatingChange: setIsEstimating,
  onClose,
}: EntryFormProps) {
  /*
   * 刪除不走 TanStack Form：它不是提交，不需要驗證，也不該被 `canSubmit` 之類的
   * 表單狀態牽動。表單只管「填好的內容送出去」這一條路。
   */
  const [isDeleting, setIsDeleting] = useState(false);

  /*
   * 估算相關狀態。同樣不走 TanStack Form —— 它不是表單的值，是「這些值從哪來」的註記。
   */
  const [estimate, setEstimate] = useState<EstimateMeta | null>(null);

  /*
   * 今日剩餘估算次數。null 代表「還不知道」——查詢失敗時就停在這個狀態，而不是
   * 假設成 0：因為查不到額度就把估算鍵鎖起來，等於讓一個附屬功能的故障拿掉主功能。
   * 真的超額時後端會回 429，那才是權威來源。
   */
  const [quota, setQuota] = useState<EstimateQuota | null>(null);

  useEffect(() => {
    let isStale = false;
    onFetchQuota()
      .then((value) => {
        if (!isStale) setQuota(value);
      })
      .catch(() => {
        /* 靜默失敗：這只影響一行提示文字，不值得打斷使用者記錄飲食 */
      });
    /* 面板關閉時可能已經 unmount，忽略遲到的回應以免對已卸載的元件設定狀態 */
    return () => {
      isStale = true;
    };
  }, [onFetchQuota]);

  const isQuotaExhausted = quota !== null && quota.remaining === 0;

  /*
   * 一律顯示剩餘次數：估算是有限資源，使用者要隨時知道還剩幾次才能自己分配。
   * null（還沒查到）時不顯示，避免顯示猜測的數字。
   */
  const quotaHint =
    quota === null
      ? null
      : quota.remaining === 0
        ? '今日估算次數已用完，明天重新計算。'
        : `今日估算還剩 ${quota.remaining} 次。`;
  const [isNutrientsOpen, setIsNutrientsOpen] = useState(false);
  /* 復原用的快照。存 ref 而非 state：它不參與渲染，變動不該觸發重繪 */
  const snapshotRef = useRef<EntryFormValues | null>(null);
  /*
   * **一份**等於幾公克（不是「一個當下單位」）。只有以「份」估算時才得知，
   * 以公克估算的結果無法推得一份多重。存 ref：不參與渲染。
   */
  const gramsPerServingRef = useRef<number | null>(
    entry?.portion.referencePortionG ?? null,
  );
  /* 聚焦當下的份量，用來算出離開欄位時該縮放多少 */
  const quantityAtFocus = useRef<string>(
    entry ? String(entry.portion.quantity) : '1',
  );
  const [presentAlert] = useIonAlert();

  const defaultValues: EntryFormValues = {
    name: entry?.name ?? '',
    calories: entry ? String(entry.calories) : '',
    quantity: entry ? String(entry.portion.quantity) : '1',
    unit: entry?.portion.unit ?? 'serving',
    proteinG: toFieldValue(entry?.nutrients.proteinG),
    fatG: toFieldValue(entry?.nutrients.fatG),
    carbsG: toFieldValue(entry?.nutrients.carbsG),
    fiberG: toFieldValue(entry?.nutrients.fiberG),
    sodiumMg: toFieldValue(entry?.nutrients.sodiumMg),
    date: entry?.date ?? defaultDate,
    /* 新增時以當下時刻預選餐別；紀錄不存時間，這只是省下多數情況的一次點選 */
    slot: entry?.slot ?? currentSlot(),
  };

  const form = useForm({
    defaultValues,
    /*
     * 驗證通過才會走到這裡。**一定要 await**——`isSubmitting` 以這個 Promise 的
     * 完成為準，不等的話按下儲存的瞬間就會解除送出中狀態，重複送出又擋不住了。
     */
    onSubmit: async ({ value }) => {
      await onSubmit({
        name: value.name.trim(),
        calories: Math.round(Number(value.calories)),
        date: value.date,
        slot: value.slot,
        portion: {
          quantity: Number(value.quantity),
          unit: value.unit,
          /* 只有估算過才知道一個單位多重；純手動輸入的紀錄沒有換算基準 */
          referencePortionG: gramsPerServingRef.current,
        },
        nutrients: {
          proteinG: parseAmount(value.proteinG),
          fatG: parseAmount(value.fatG),
          carbsG: parseAmount(value.carbsG),
          fiberG: parseAmount(value.fiberG),
          sodiumMg: parseAmount(value.sodiumMg),
        },
      });
    },
    /* 驗證沒過時的觸覺回饋。視覺回饋是欄位下方的訊息，由各欄位自己渲染 */
    onSubmitInvalid: () => haptic('error'),
  });

  /*
   * 這四個值有欄位以外的用途（查候選、切換單位、停用整個表單），故訂閱到元件層級，
   * 而非只在 `form.Field` 的 render prop 裡取用。
   *
   * 分開四次 `useSelector`、每次只取一個原始值，是為了讓比較能停在值本身——
   * 合成一個物件回傳的話，每次 store 更新都會得到新的參照，等於每打一個字整個
   * 表單重繪。品項與熱量的輸入因此只重繪自己那一個 `form.Field`。
   */
  const isSubmitting = useSelector(form.store, (state) => state.isSubmitting);
  const date = useSelector(form.store, (state) => state.values.date);
  const slot = useSelector(form.store, (state) => state.values.slot);
  const unit = useSelector(form.store, (state) => state.values.unit);

  const isEditing = entry !== null;
  /*
   * 估算中一併視為忙碌。估算會回填六個欄位，使用者在等待期間改的任何東西都會被
   * 覆蓋掉 —— 那是「東西自己變了」的體感。停用欄位是防止那件事發生，
   * 估算中的遮罩（ProgressOverlay）則負責讓「現在不能動」看得出來。
   */
  const isBusy = isSubmitting || isDeleting || isEstimating;

  /*
   * 候選跟著餐別與日期走，改哪一個都要重算。掃的是近 90 天的全部紀錄，故加上 memo。
   */
  const recentItems = useMemo(
    () => (isEditing ? [] : getRecentItems(date, slot)),
    [isEditing, getRecentItems, date, slot],
  );

  /**
   * 以 AI 估算並帶入。
   *
   * 一次覆蓋熱量與五個營養素，而不是「只填空的欄位」：後者的規則使用者猜不到
   * （為什麼這格變了那格沒變？），而覆蓋 + 一顆復原鍵是可預期的。
   */
  const handleEstimate = async () => {
    const foodName = form.getFieldValue('name').trim();
    const quantityValue = Number(form.getFieldValue('quantity'));
    if (
      foodName === '' ||
      !Number.isFinite(quantityValue) ||
      quantityValue <= 0 ||
      isBusy ||
      isEstimating
    ) {
      return;
    }

    const unit = form.getFieldValue('unit');

    setIsEstimating(true);
    try {
      /*
       * 份量一起送給模型。兩種單位的價值不同：公克讓模型不必猜「一份多大」
       * （那是估算變異最大的來源），份數只是把結果等比放大。
       */
      const { estimate: result, quota } = await onEstimate(
        foodName,
        quantityValue,
        unit,
      );

      /* 用回應帶回的額度，不另外重查：那一次往返之間額度可能又被別的分頁改動 */
      setQuota(quota);

      /* 先存快照再寫入，否則復原時拿到的是已經被覆蓋的值 */
      snapshotRef.current = form.state.values;

      form.setFieldValue('calories', String(Math.round(result.caloriesKcal)));
      form.setFieldValue('proteinG', toFieldValue(result.proteinG));
      form.setFieldValue('fatG', toFieldValue(result.fatG));
      form.setFieldValue('carbsG', toFieldValue(result.carbsG));
      form.setFieldValue('fiberG', toFieldValue(result.fiberG));
      form.setFieldValue('sodiumMg', toFieldValue(result.sodiumMg));

      /*
       * 只有以「份」估算才學得到一份多重：回傳的是這次的總公克數，除以份數即得。
       * 以公克估算時使用者已經給了確切重量，模型沒有（也不需要）告訴我們一份多大。
       */
      if (unit === 'serving') {
        gramsPerServingRef.current = result.portionGrams / quantityValue;
      }

      setEstimate({ confidence: result.confidence, notes: result.notes });
      /* 一定要展開：收合著填等於背著使用者改東西 */
      setIsNutrientsOpen(true);
      haptic('success');
    } catch (error) {
      haptic('error');
      /*
       * 錯誤訊息依狀態碼分三種（語意見 diet-service README）：額度用完、可重試（5xx
       * 與網路錯誤）、換個說法（其餘 4xx）。額度用完時一併歸零，估算鍵才會停用。
       */
      if (error instanceof ApiError && error.status === 429) {
        setQuota({ limit: 0, used: 0, remaining: 0 });
        presentAlert({
          header: '今日估算次數已用完',
          message: '明天會重新計算。這段期間可以直接手動填寫營養素。',
          buttons: ['知道了'],
        });
        return;
      }

      const isRetryable = !(error instanceof ApiError) || error.status >= 500;
      presentAlert({
        header: isRetryable ? '估算服務暫時忙碌' : '無法估算這個品項',
        message: isRetryable
          ? '請稍後再試，或直接手動填寫。'
          : '換個說法試試，或直接手動填寫。',
        buttons: ['知道了'],
      });
    } finally {
      setIsEstimating(false);
    }
  };

  /**
   * 改份量時，把熱量與營養素等比縮放。
   *
   * 不縮放的話「1.5 份」只是個標籤，熱量還是一份的量 —— 那比不做這個功能更糟。
   *
   * 只在**已經有數值**時縮放：空欄位縮放後還是空的，但若熱量是使用者自己填的，
   * 改份數同樣該跟著變，故不限定於估算過的紀錄。
   */
  const rescaleBy = (ratio: number) => {
    if (!Number.isFinite(ratio) || ratio <= 0 || ratio === 1) return;

    const scale = (name: keyof EntryFormValues) => {
      const current = Number(form.getFieldValue(name));
      if (
        !Number.isFinite(current) ||
        String(form.getFieldValue(name)).trim() === ''
      ) {
        return;
      }
      const next = current * ratio;
      form.setFieldValue(
        name,
        /* 熱量取整、營養素留一位小數：再多的精度在估算值上是假的 */
        name === 'calories'
          ? String(Math.round(next))
          : String(Math.round(next * 10) / 10),
      );
    };

    scale('calories');
    scale('proteinG');
    scale('fatG');
    scale('carbsG');
    scale('fiberG');
    scale('sodiumMg');
  };

  /**
   * 切換單位。**永遠可切** —— 使用者本來就知道自己吃了多少，估算只是輔助。
   *
   * 有換算基準時順便把數值換算過去（1 份 → 280 公克）；沒有基準就清空讓使用者自己填。
   * 保留原數字更糟：「1 份」直接變成「1 公克」是個明確錯誤的值，而空白至少是誠實的。
   */
  const handleUnitChange = (next: MeasurementUnit) => {
    const current = form.getFieldValue('unit');
    if (next === current) return;

    const gramsPerServing = gramsPerServingRef.current;
    const quantity = Number(form.getFieldValue('quantity'));
    const canConvert =
      gramsPerServing !== null &&
      gramsPerServing > 0 &&
      Number.isFinite(quantity) &&
      quantity > 0;

    /* 換算的是「同一個總量的另一種說法」，故熱量與營養素不動，只換份量的表達方式 */
    const nextQuantity = canConvert
      ? String(
          Math.round(
            (next === 'gram'
              ? quantity * gramsPerServing
              : quantity / gramsPerServing) * 100,
          ) / 100,
        )
      : '';

    form.setFieldValue('unit', next);
    form.setFieldValue('quantity', nextQuantity);
    /*
     * 同步基準線，否則接下來的 blur 會把「1 → 280」當成使用者調整份量，
     * 把熱量乘上 280 倍。換單位不是改份量。
     */
    quantityAtFocus.current = nextQuantity;
    haptic('tap');
  };

  /** 復原整批估算：回到按下按鈕之前的表單狀態。 */
  const handleUndoEstimate = () => {
    const snapshot = snapshotRef.current;
    if (snapshot === null) return;

    form.setFieldValue('calories', snapshot.calories);
    form.setFieldValue('proteinG', snapshot.proteinG);
    form.setFieldValue('fatG', snapshot.fatG);
    form.setFieldValue('carbsG', snapshot.carbsG);
    form.setFieldValue('fiberG', snapshot.fiberG);
    form.setFieldValue('sodiumMg', snapshot.sodiumMg);

    snapshotRef.current = null;
    setEstimate(null);
    haptic('tap');
  };

  const handleDelete = async () => {
    if (isBusy || !onDelete || entry === null) return;

    setIsDeleting(true);
    try {
      await onDelete(entry.id);
    } finally {
      /*
       * 成功時 DietApp 會關閉面板、本元件隨即卸載，這次 setState 是空轉；留著才能
       * 涵蓋失敗的路徑——那時面板還開著，必須解除停用讓使用者能直接再試一次。
       */
      setIsDeleting(false);
    }
  };

  /* 帶入候選：直接寫進表單狀態，寫入本身會觸發該欄位的 onChange 驗證，錯誤自然消失 */
  const applyRecentItem = (item: RecentItem) => {
    form.setFieldValue('name', item.name);
    form.setFieldValue('calories', String(item.calories));

    /*
     * 份量必須跟著帶：上次記的若是「200 公克」，只帶熱量回來卻顯示「1 份」，
     * 那個熱量就掛在錯誤的份量標籤下，而使用者無從發現。
     */
    form.setFieldValue('quantity', String(item.portion.quantity));
    form.setFieldValue('unit', item.portion.unit);
    quantityAtFocus.current = String(item.portion.quantity);
    gramsPerServingRef.current = item.portion.referencePortionG;

    form.setFieldValue('proteinG', toFieldValue(item.nutrients.proteinG));
    form.setFieldValue('fatG', toFieldValue(item.nutrients.fatG));
    form.setFieldValue('carbsG', toFieldValue(item.nutrients.carbsG));
    form.setFieldValue('fiberG', toFieldValue(item.nutrients.fiberG));
    form.setFieldValue('sodiumMg', toFieldValue(item.nutrients.sodiumMg));

    /*
     * 帶入營養素就展開，理由同 AI 估算：收合著填等於背著使用者改東西。
     * 沒有營養素時不動展開狀態 —— 他可能已經自己打開在填了。
     */
    if (hasMacros(item.nutrients)) setIsNutrientsOpen(true);

    /* 這批值是使用者自己記過的，不是模型估的，故不掛 AI 來源橫幅 */
    setEstimate(null);
    snapshotRef.current = null;

    haptic('tap');
  };

  return (
    <>
      <SheetLayout
        title={isEditing ? '編輯紀錄' : '新增紀錄'}
        headerEnd={
          isEditing &&
          onDelete && (
            <IonButton
              onClick={handleDelete}
              disabled={isBusy}
              className="text-muted-foreground"
            >
              {/*
                只換圖示、文字維持「刪除」：這顆鍵在 header 內由內容決定寬度，而
                iOS mode 的 ion-title 是絕對定位置中、只留 90px 內距給兩側按鈕，
                換成較長的字有壓到標題的風險。轉圈本身已足夠表達進行中。
              */}
              {isDeleting ? (
                <IonSpinner name="crescent" className="h-4 w-4" />
              ) : (
                <TrashIcon size={16} />
              )}
              <span className="ml-1.5 text-sm">刪除</span>
            </IonButton>
          )
        }
        /* 儲存鍵以 form 屬性關聯到表單，不必身為其子節點也能提交 */
        footer={
          <>
            <IonButton
              fill="outline"
              onClick={onClose}
              disabled={isBusy}
              className="flex-1"
            >
              取消
            </IonButton>

            {/*
              **不用 `canSubmit` 控制停用。** 那會讓有錯的表單連按都按不下去，錯誤
              訊息也就永遠不會出現——使用者只看到一顆按不動的鍵，不知道要改什麼。
              按得下去、按了才驗、驗完指出哪裡不對，才是可操作的順序。

              進行中保留文字而非只留轉圈：純圖示的按鈕沒有可讀名稱，螢幕閱讀器會念成
              「按鈕」。兩顆鍵都是 flex-1，換字不會讓寬度跳動。
            */}
            <IonButton
              type="submit"
              form={FORM_ID}
              disabled={isBusy}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <IonSpinner name="crescent" className="h-4 w-4" />
                  <span className="ml-2">儲存中…</span>
                </>
              ) : (
                '儲存'
              )}
            </IonButton>
          </>
        }
      >
        <form
          id={FORM_ID}
          /*
           * 送出中仍擋一次：表單能由鍵盤 Enter 送出，那條路徑繞過按鈕的 `disabled`。
           * 驗證與 pending 狀態都交給 form.handleSubmit()，這裡只負責攔住原生提交。
           */
          onSubmit={(event) => {
            event.preventDefault();
            if (isBusy) return;
            void form.handleSubmit();
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          {/*
            日期與餐別放在表單最上方：下方的常吃品項清單由這兩個值查出，
            控制項與結果相鄰，切換時才看得出因果。

            兩者不包 `form.Field`：沒有驗證規則，值又要在元件層級使用。
            值仍存在表單狀態，以 useSelector 讀、setFieldValue 寫。
          */}
          <div className={CONTEXT_BAR_CLASS}>
            {/*
              日期走 Ionic 的面板，不用原生 `<input type="date">`。

              原生 input 的彈出層由瀏覽器繪製（iOS 是滾輪、桌機各家不同），吃不到深色
              主題；而頂部日期列已經是 Ionic 的月曆，混用會讓「點日期」在兩個地方開出
              長相不同的東西。
            */}
            <IonDatetimeButton
              datetime={DATE_PICKER_ID}
              disabled={isBusy}
              aria-label="日期"
              className={DATETIME_BUTTON_CLASS}
            />

            <SegmentedControl
              value={slot}
              options={MEAL_SLOTS.map((option) => ({
                value: option,
                label: (
                  <span className="text-xs">{MEAL_SLOT_LABELS[option]}</span>
                ),
              }))}
              onChange={(next) => form.setFieldValue('slot', next)}
              disabled={isBusy}
              ariaLabel="餐別"
              /* 關掉滑動切換：正下方的常吃品項可左右滑動，兩者手勢會互搶 */
              swipeGesture={false}
              className="w-full"
            />
          </div>

          {/*
            `keepContentsMounted` 是 `IonDatetimeButton` 的硬性要求——按鈕上顯示的
            文字讀自 `IonDatetime`，內容若延到開啟才掛載，按鈕在第一次開啟前會是空的。

            用置中的自動高度 modal 而非底部面板：這裡已經在記錄面板（本身就是底部
            面板）之內，再疊一層底部面板會變成兩片由下往上的板子，看不出層級。
          */}
          <IonModal keepContentsMounted className={DATETIME_MODAL_CLASS}>
            <IonDatetime
              id={DATE_PICKER_ID}
              presentation="date"
              locale="zh-TW"
              value={date}
              className={DATETIME_PICKER_CLASS}
              onIonChange={(event) => {
                const next = readDateTimeValue(event.detail.value);
                if (next === null) return;
                form.setFieldValue('date', toDateKeyFromISO(next) ?? date);
              }}
            />
          </IonModal>
          {/*
            只在新增時渲染，且沒有候選也留一列同高的提示，切換餐別時下方表單才不會上下跳動。
          */}
          {!isEditing && (
            <div className="flex flex-col gap-2">
              {/* 標題點出候選的來源，否則切換餐別時清單整排換掉會顯得沒有道理 */}
              <span className={LABEL_CLASS}>
                最近的{MEAL_SLOT_LABELS[slot]}
              </span>
              {/*
                橫向捲動而非換行：讓候選固定佔一行，不把下方欄位推出畫面。
                空狀態的提示與 chip 同樣是 min-h-11，兩者高度因此對齊。
              */}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {recentItems.length > 0 ? (
                  recentItems.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => applyRecentItem(item)}
                      disabled={isBusy}
                      className={CHIP_CLASS}
                    >
                      {item.name}
                      <span className="font-mono text-[0.625rem] tabular-nums text-muted-foreground">
                        {formatCalories(item.calories)}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className={RECENT_EMPTY_CLASS}>
                    最近沒有記錄過的{MEAL_SLOT_LABELS[slot]}品項
                  </p>
                )}
              </div>
            </div>
          )}

          {/*
            **同一個驗證函式同時掛 onChange 與 onSubmit**，兩者少了誰都不行：
            只掛 onSubmit，使用者改到正確之後訊息仍留在畫面上直到下次送出；
            只掛 onChange，從沒碰過的欄位在送出時不會被驗（onChange 只在值變動時跑），
            空白的表單會直接被送出去。

            未動過的欄位不會顯示錯誤，因為 onChange 尚未跑過、meta.errors 是空的——
            不必額外用 isTouched 去擋。
          */}
          <form.Field
            name="name"
            validators={{ onChange: validateName, onSubmit: validateName }}
          >
            {(field) => (
              <div className="flex flex-col gap-1.5">
                {/*
                  估算鍵放在 <label> **外面**：放在裡面的話，點按鈕會連帶觸發 label
                  的聚焦行為，於是每按一次估算就順便叫出鍵盤，把剛展開的結果推出畫面。
                */}
                <div className="flex items-end gap-2">
                  {/*
                    標籤放在欄位外面，不用 IonInput 的 `labelPlacement="stacked"`：
                    其浮動標籤位移是為 `fill` 模式校準的，iOS mode 下會與輸入值重疊。
                    以 <label> 包住，點標籤即可聚焦欄位。
                  */}
                  <label className="flex flex-1 flex-col gap-1.5">
                    <span className={LABEL_CLASS}>品項</span>
                    <IonInput
                      aria-label="品項"
                      value={field.state.value}
                      onIonInput={(event) =>
                        field.handleChange(event.detail.value ?? '')
                      }
                      onIonBlur={field.handleBlur}
                      placeholder="例如：雞胸肉便當"
                      autocomplete="off"
                      disabled={isBusy}
                      className={FIELD_CLASS}
                    />
                  </label>

                  {/* 緊鄰品項欄位：估算以品項為輸入，相鄰即說明「要先填品項」 */}
                  <button
                    type="button"
                    onClick={handleEstimate}
                    disabled={
                      field.state.value.trim() === '' ||
                      isBusy ||
                      isEstimating ||
                      isQuotaExhausted
                    }
                    aria-label={
                      isQuotaExhausted
                        ? '今日估算次數已用完'
                        : '以 AI 估算熱量與營養素'
                    }
                    className={cn(
                      'flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-md px-3',
                      'border border-border bg-card text-xs text-accent',
                      'transition-all duration-200 ease-out',
                      'hover:border-border-hover active:scale-[0.98]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'disabled:pointer-events-none disabled:opacity-40',
                    )}
                  >
                    {isEstimating ? (
                      <IonSpinner name="crescent" className="size-3.5" />
                    ) : (
                      <SparkleIcon className="size-3.5" />
                    )}
                    估算
                  </button>
                </div>
                {/* 字級與顏色壓低：這是資訊而非警告，用完時由 429 的 alert 中斷使用者 */}
                {quotaHint !== null && (
                  <p className="mt-1.5 text-[0.6875rem] text-muted-foreground">
                    {quotaHint}
                  </p>
                )}
                <FieldError message={field.state.meta.errors[0]} />
              </div>
            )}
          </form.Field>

          {/*
            份量在熱量之前：份量決定熱量的意義（820 卡是一份還是兩份的量）。
            兩者各佔一整列：份量列還有單位切換，並排會把輸入框壓得太窄。
          */}
          <form.Field
            name="quantity"
            validators={{
              onChange: validateQuantity,
              onSubmit: validateQuantity,
            }}
          >
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <span className={LABEL_CLASS}>份量</span>
                <div className="flex items-center gap-2">
                  <IonInput
                    aria-label="份量"
                    value={field.state.value}
                    onIonInput={(event) =>
                      field.handleChange(event.detail.value ?? '')
                    }
                    /*
                      在 blur 而非每次按鍵時縮放：邊打邊縮放的話，輸入「1.5」會在
                      打到「1.」的瞬間先縮放一次，數值就再也對不回去了。
                    */
                    onIonFocus={() => {
                      quantityAtFocus.current = field.state.value;
                    }}
                    onIonBlur={() => {
                      field.handleBlur();
                      const next = Number(field.state.value);
                      const previous = Number(quantityAtFocus.current);
                      if (
                        Number.isFinite(next) &&
                        Number.isFinite(previous) &&
                        previous > 0
                      ) {
                        rescaleBy(next / previous);
                      }
                      quantityAtFocus.current = field.state.value;
                    }}
                    type="number"
                    inputmode="decimal"
                    min={0.01}
                    max={10000}
                    step="0.01"
                    disabled={isBusy}
                    className={cn(FIELD_CLASS, 'flex-1 font-mono tabular-nums')}
                  />

                  <SegmentedControl
                    value={unit}
                    options={UNIT_OPTIONS}
                    onChange={handleUnitChange}
                    disabled={isBusy}
                    ariaLabel="份量單位"
                    className="w-[9.5rem] shrink-0"
                  />
                </div>
                <FieldError message={field.state.meta.errors[0]} />
              </div>
            )}
          </form.Field>

          <form.Field
            name="calories"
            validators={{
              onChange: validateCalories,
              onSubmit: validateCalories,
            }}
          >
            {(field) => (
              <label className="flex flex-col gap-1.5">
                <span className={LABEL_CLASS}>熱量（kcal）</span>
                <IonInput
                  aria-label="熱量（kcal）"
                  value={field.state.value}
                  onIonInput={(event) =>
                    field.handleChange(event.detail.value ?? '')
                  }
                  onIonBlur={field.handleBlur}
                  type="number"
                  inputmode="numeric"
                  min={0}
                  step="1"
                  placeholder="0"
                  disabled={isBusy}
                  className={cn(FIELD_CLASS, 'font-mono tabular-nums')}
                />
                <FieldError message={field.state.meta.errors[0]} />
              </label>
            )}
          </form.Field>

          <NutrientSection
            isOpen={isNutrientsOpen}
            onToggle={() => setIsNutrientsOpen((open) => !open)}
            estimate={estimate}
            onUndoEstimate={handleUndoEstimate}
          >
            {/*
              三大營養素一組、纖維與鈉一組：前三者組成熱量、彼此可比，後兩者是獨立指標。
              分兩排也讓每格有足夠寬度顯示標籤，不必縮字級。
            */}
            <div className="grid grid-cols-3 gap-2.5">
              <form.Field
                name="proteinG"
                validators={{
                  onChange: validateGrams,
                  onSubmit: validateGrams,
                }}
              >
                {(field) => (
                  <NutrientField
                    label="蛋白質"
                    unit="g"
                    value={field.state.value}
                    error={field.state.meta.errors[0]}
                    disabled={isBusy}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
              <form.Field
                name="fatG"
                validators={{
                  onChange: validateGrams,
                  onSubmit: validateGrams,
                }}
              >
                {(field) => (
                  <NutrientField
                    label="脂肪"
                    unit="g"
                    value={field.state.value}
                    error={field.state.meta.errors[0]}
                    disabled={isBusy}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
              <form.Field
                name="carbsG"
                validators={{
                  onChange: validateGrams,
                  onSubmit: validateGrams,
                }}
              >
                {(field) => (
                  <NutrientField
                    label="碳水"
                    unit="g"
                    value={field.state.value}
                    error={field.state.meta.errors[0]}
                    disabled={isBusy}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <form.Field
                name="fiberG"
                validators={{
                  onChange: validateGrams,
                  onSubmit: validateGrams,
                }}
              >
                {(field) => (
                  <NutrientField
                    label="膳食纖維"
                    unit="g"
                    value={field.state.value}
                    error={field.state.meta.errors[0]}
                    disabled={isBusy}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
              <form.Field
                name="sodiumMg"
                validators={{
                  onChange: validateSodium,
                  onSubmit: validateSodium,
                }}
              >
                {(field) => (
                  <NutrientField
                    label="鈉"
                    unit="mg"
                    value={field.state.value}
                    error={field.state.meta.errors[0]}
                    disabled={isBusy}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                )}
              </form.Field>
            </div>
          </NutrientSection>
        </form>
      </SheetLayout>

      {/* 疊在面板之上：與面板內容並列，才蓋得到標題列上的按鈕 */}
      {isEstimating && (
        <ProgressOverlay
          icon={<SparkleIcon className="size-5" />}
          title="AI 估算中"
          message="正在查詢並計算營養成分，通常需要幾秒。"
          slowMessage="比平常久一些，仍在處理中。"
        />
      )}
    </>
  );
}

const UNIT_OPTIONS: readonly { value: MeasurementUnit; label: string }[] = [
  { value: 'serving', label: '份' },
  { value: 'gram', label: '公克/毫升' },
];

/**
 * `IonDatetimeButton` 靠 id 找到它要開的 `IonDatetime`，故兩者必須配對。
 *
 * 表單一次只有一份實例（面板關閉時整個卸載），不會有 id 相撞的問題。
 */
const DATE_PICKER_ID = 'entry-date-picker';

/**
 * 選擇器面板：置中的自動高度 modal。
 *
 * `fit-content` 讓面板貼著月曆的實際尺寸，不必為不同月份的週數（五週或
 * 六週）各猜一個高度——猜太矮會被裁掉，猜太高則下方留一片空白。
 *
 * 底色刻意與 `ion-datetime` 自己的預設底色（`--ion-color-light`，見 @mobile/theme）
 * 取同一個 token，而非另給一個顏色。時間輪上下緣的淡出漸層吃的是
 * `--background-rgb`，那個變數只認得色值三元組、跟著 datetime 的預設走；面板若用
 * 別的底色，漸層就會淡向一個與實際背景不同的顏色，在輪子邊緣留下一圈色差。
 */
const DATETIME_MODAL_CLASS = cn(
  '[--width:fit-content]',
  '[--height:fit-content]',
  '[--border-radius:var(--radius-lg)]',
  '[--background:var(--color-card-solid)]',
);

/**
 * 面板內的選擇器：四周留白。
 *
 * 內距下在 `ion-datetime` 而非 modal 上——modal 的內容是放進 shadow DOM 的
 * wrapper 裡的，host 的 padding 推不動它。而 `--width/--height: fit-content`
 * 是量內容的尺寸，內距下在內容上，面板就會跟著一起長大，不必另外調面板尺寸。
 *
 * 底色留給 modal painting（兩者本來就同一個 token），選擇器自身透明，
 * 內距區域才不會因為多疊一層而出現色階差。
 */
const DATETIME_PICKER_CLASS = 'p-3 [--background:transparent]';

/**
 * 觸發鍵。
 *
 * `app-datetime` 的樣式在 `@mobile/theme` 的 `ionic-overrides.css`——該元件是 shadow
 * 且不暴露 CSS 變數，只能從那裡透過繼承的變數影響它。`self-start` 則是版面：情境列
 * 是 flex column，不指定會被拉滿整欄——日期是一句短文字，撐成整行寬的按鈕會讓它看
 * 起來跟下方的餐別選擇器同等重要。
 */
const DATETIME_BUTTON_CLASS = 'app-datetime self-start';

/**
 * 情境列：日期在上、餐別在下，收在同一個框裡，表示「這筆紀錄歸在哪」這組前提。
 *
 * 兩者不並排：窄螢幕上與日期鍵同行時，四個餐別鍵的寬度不足以完整顯示文字。
 *
 * 不另加「日期」「餐別」標籤：兩個控制項的內容本身即自明，螢幕閱讀器由 `aria-label` 交代。
 */
const CONTEXT_BAR_CLASS = cn(
  'flex flex-col gap-2 rounded-xl',
  'border border-border bg-card p-2',
);

/**
 * 取出 `IonDatetime` 的單日選取值。
 *
 * `event.detail.value` 的型別涵蓋了 `multiple` 模式的字串陣列。本表單不開那個模式，
 * 真出現陣列也不該猜要用哪一個，故一律忽略。
 */
function readDateTimeValue(value: string | string[] | null | undefined) {
  return typeof value === 'string' ? value : null;
}

/**
 * 候選清單的空狀態：與 `CHIP_CLASS` 同高（`min-h-11`），有無候選都不改變高度。
 * 虛線邊框表示「目前是空的」，而非一顆可點的鍵。
 */
const RECENT_EMPTY_CLASS = cn(
  'flex min-h-11 items-center rounded-full',
  'border border-dashed border-border px-3.5 text-xs text-muted-foreground',
);

/**
 * 表單裡的五個營養素欄位共用的樣式與結構。全部選填，故標籤不帶必填記號。
 *
 * 刻意不吃表單實例，只吃值與回呼：這樣它不必知道 TanStack Form 的存在，
 * 型別也不必去描述一個泛型參數極多的表單物件。
 */
function NutrientField({
  label,
  unit,
  value,
  error,
  disabled,
  onChange,
  onBlur,
}: {
  label: string;
  unit: string;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className={LABEL_CLASS}>
        {label}
        <span className="ml-0.5 text-muted-foreground/70">({unit})</span>
      </span>
      <IonInput
        aria-label={`${label}（${unit}）`}
        value={value}
        onIonInput={(event) => onChange(event.detail.value ?? '')}
        onIonBlur={onBlur}
        type="number"
        inputmode="decimal"
        min={0}
        placeholder="—"
        disabled={disabled}
        className={cn(FIELD_CLASS, 'font-mono tabular-nums')}
      />
      <FieldError message={error} />
    </label>
  );
}
